import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { BadgeCheck, Loader2, ShieldQuestion, Wrench } from 'lucide-react';
import { versions } from '../../services/superuserApi';

// Alvos de toque ≥ 44 px e texto ≥ 12 px: este painel é usado no telemóvel, a 390 px.
const TAP = 44;

const apiMessage = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

const fmtDate = (value) => (value
  ? new Date(value).toLocaleString('pt-PT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

/**
 * A cor de cada veredicto. São cinco respostas diferentes e todas importam: «sem contentor»,
 * «parada», «desvio» e «desconhecido» pedem acções diferentes — colapsá-las num «erro» obrigava
 * a ir ao servidor ver qual era.
 */
const statusStyle = (status) => {
  switch (status) {
    case 'OK':
      return { background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)' };
    case 'DRIFT':
      return { background: 'var(--warning-bg, #fff7e6)', color: 'var(--warning-text, #8a5a00)' };
    case 'MISSING':
    case 'STOPPED':
      return { background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)' };
    default:
      return { background: 'var(--bg-tertiary, #f2f2f2)', color: 'var(--text-muted, #6b6b6b)' };
  }
};

/**
 * Versão REAL de uma instância: o que o portal aplicou (release + imagem), o resultado da última
 * comparação com o servidor e a reparação a partir do registo.
 *
 * «Verificar» só lê. «Reparar» recria o contentor a partir da release REGISTADA — nunca de uma
 * escolhida agora, para não transformar um conserto numa actualização disfarçada — e só com uma
 * confirmação explícita, porque a instância fica indisponível durante a recriação.
 */
const VersionPanel = ({ instanceId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [repairOpen, setRepairOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairError, setRepairError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await versions.state(instanceId);
      setData(res.data);
    } catch (e) {
      setLoadError(apiMessage(e, 'Não foi possível ler a versão desta instância.'));
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setVerifying(true);
    try {
      const res = await versions.verify(instanceId);
      toast.info(`Verificação: ${res.data?.statusLabel || res.data?.status || '—'}`);
      await load();
    } catch (e) {
      toast.error(apiMessage(e, 'A verificação não foi feita.'));
    } finally {
      setVerifying(false);
    }
  };

  const openRepair = () => {
    setConfirmed(false);
    setRepairError(null);
    setRepairOpen(true);
  };

  const confirmRepair = async () => {
    if (!confirmed) return;
    setRepairing(true);
    setRepairError(null);
    try {
      const res = await versions.repair(instanceId, true);
      toast.success(res.data?.message || 'Reparação concluída.');
      setRepairOpen(false);
      await load();
    } catch (e) {
      // Uma recusa tem motivo e fica à frente de quem pediu: não se fecha o diálogo em silêncio.
      setRepairError(apiMessage(e, 'A reparação não foi feita.'));
      await load();
    } finally {
      setRepairing(false);
    }
  };

  const status = data?.status;
  const chip = statusStyle(status);

  return (
    <div data-testid="version-panel" style={styles.card}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <BadgeCheck size={18} color="var(--accent-primary)" style={{ flexShrink: 0 }} />
          <h3 style={styles.title}>Versão e desvio</h3>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="version-verify"
            onClick={verify}
            disabled={verifying || loading}
            style={{ ...styles.secondary, opacity: verifying || loading ? 0.6 : 1 }}
          >
            {verifying ? 'A verificar…' : 'Verificar'}
          </button>
          <button
            type="button"
            data-testid="version-repair-open"
            onClick={openRepair}
            disabled={!data?.registered || loading}
            title={data?.registered ? '' : 'Sem release registada não há contra o que reparar'}
            style={{ ...styles.primary, opacity: !data?.registered || loading ? 0.5 : 1 }}
          >
            <Wrench size={14} /> Reparar
          </button>
        </div>
      </div>

      {loading && !data && (
        <div style={styles.muted}><Loader2 size={16} className="spin" /> A ler a versão…</div>
      )}

      {!loading && loadError && (
        <div style={styles.errorBox}>
          <div>{loadError}</div>
          <button type="button" onClick={load} style={{ ...styles.secondary, marginTop: '0.5rem' }}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !loadError && data && (
        <>
          <div data-testid="version-registered" style={styles.registered}>
            {data.registered ? (
              <>
                <div>
                  <strong>Release aplicada:</strong> {data.releaseVersion || '—'}{' '}
                  <code style={styles.code}>{data.imageTag}</code>
                </div>
                <div style={styles.mutedSmall}>
                  Aplicada em {fmtDate(data.appliedAt)}
                  {data.appliedBy ? ` por ${data.appliedBy}` : ''}
                  {data.source ? ` · ${data.source === 'Repair' ? 'reparação' : 'instalação/actualização'}` : ''}
                </div>
              </>
            ) : (
              <div data-testid="version-unregistered" style={styles.warningBox}>
                <ShieldQuestion size={16} style={{ flexShrink: 0 }} />
                <div>
                  Sem release registada: esta instância foi instalada antes de o portal guardar a
                  versão aplicada. Instale a partir de uma release para passar a haver um registo
                  contra o qual comparar e reparar.
                </div>
              </div>
            )}
          </div>

          <div data-testid="version-check" style={styles.checkLine}>
            <span style={{ ...styles.chip, ...chip }}>{data.statusLabel}</span>
            {data.checkedAt ? (
              <span style={styles.mutedSmall}>verificada em {fmtDate(data.checkedAt)}</span>
            ) : (
              <span style={styles.mutedSmall}>ainda não foi verificada</span>
            )}
          </div>

          {data.reason && (
            <div
              data-testid="version-reason"
              style={status === 'OK' ? styles.okBox : styles.warningBox}
            >
              {data.reason}
            </div>
          )}
        </>
      )}

      {repairOpen && (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Reparar instância">
          <div style={styles.dialog}>
            <h4 style={styles.title}>Reparar a partir do registo?</h4>
            <p style={styles.warning}>
              O contentor é recriado a partir de <strong>{data?.releaseVersion || '—'}</strong>{' '}
              (<code style={styles.code}>{data?.imageTag || '—'}</code>). A base de dados
              <strong> não é apagada</strong>, mas a instância fica indisponível enquanto é recriada.
              Uma reparação recusa-se a correr se a instância já estiver conforme.
            </p>

            <label htmlFor="repair-confirm" style={styles.checkLabel}>
              <input
                id="repair-confirm"
                data-testid="version-repair-confirm"
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                style={styles.checkbox}
              />
              <span>Confirmo que quero recriar o contentor agora</span>
            </label>

            {repairError && <div style={styles.errorBox}>{repairError}</div>}

            <div style={styles.actions}>
              <button type="button" onClick={() => setRepairOpen(false)} disabled={repairing} style={styles.secondary}>
                Cancelar
              </button>
              <button
                type="button"
                data-testid="version-repair-submit"
                onClick={confirmRepair}
                disabled={!confirmed || repairing}
                style={{ ...styles.primary, opacity: !confirmed || repairing ? 0.5 : 1 }}
              >
                {repairing ? 'A reparar…' : 'Reparar agora'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    background: 'var(--bg-secondary, #fff)',
    border: '1px solid var(--border, #e3e3e3)',
    borderRadius: '16px',
    padding: '1rem',
    marginTop: '1rem',
    color: 'var(--text-primary, #1c1c1c)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1rem', fontWeight: 700 },
  registered: { marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: 1.6 },
  checkLine: { marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  chip: { padding: '2px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700 },
  muted: {
    marginTop: '0.75rem', fontSize: '0.85rem', color: 'var(--text-muted, #6b6b6b)',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  mutedSmall: { fontSize: '0.78rem', color: 'var(--text-muted, #6b6b6b)' },
  warningBox: {
    marginTop: '0.5rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--warning-bg, #fff7e6)', color: 'var(--warning-text, #8a5a00)',
    display: 'flex', gap: '0.5rem', alignItems: 'flex-start', lineHeight: 1.55,
  },
  okBox: {
    marginTop: '0.5rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--success-bg, #e7f6ec)', color: 'var(--success-text, #146c2e)', lineHeight: 1.55,
  },
  errorBox: {
    marginTop: '0.75rem', padding: '0.75rem', borderRadius: '10px', fontSize: '0.85rem',
    background: 'var(--error-bg, #fdeaea)', color: 'var(--error-text, #a11616)', lineHeight: 1.5,
  },
  primary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px', border: 'none',
    background: 'var(--accent-primary, #d4a017)', color: '#fff', fontWeight: 700, fontSize: '0.875rem',
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
  },
  secondary: {
    minHeight: TAP, minWidth: TAP, padding: '0 1rem', borderRadius: '10px',
    border: '1px solid var(--border, #e3e3e3)', background: 'transparent',
    color: 'var(--text-primary, #1c1c1c)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
  },
  code: { fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000,
  },
  dialog: {
    background: 'var(--bg-secondary, #fff)', color: 'var(--text-primary, #1c1c1c)',
    borderRadius: '16px', padding: '1.25rem', width: '100%', maxWidth: '520px', maxHeight: '90vh',
    overflowY: 'auto',
  },
  warning: { fontSize: '0.85rem', lineHeight: 1.6, margin: '0.75rem 0' },
  checkLabel: {
    display: 'inline-flex', alignItems: 'center', gap: '0.5rem', minHeight: TAP,
    fontSize: '0.875rem', fontWeight: 600,
  },
  checkbox: { width: '24px', height: '24px' },
  actions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1rem', flexWrap: 'wrap' },
};

export default VersionPanel;
